import { Grid, ToggleButton, toggleButtonClasses, ToggleButtonGroup, toggleButtonGroupClasses } from "@mui/material";
import { forwardRef, memo, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ButtonCard, formatDataCompare, formatDate, formatDateTimeFile, formatDecimal, formatMonetary, isMobileDevice, toastError } from "../../../commons/utils";
import BarChart from "../../../charts/Bar";
import { Input } from "../../../components/Input";
import PieChart from "../../../charts/pie";
import moment from "moment";
import { Columns, GridCard } from "..";
import { ExCard } from "../../../charts/Card";
import { model_imobilizado, model_imobilizado_categoria, model_imobilizado_subcategoria, model_fornecedor, model_status, model_motivo_requisicao, model_usuario, model_empresa } from "../../../models";
import { useAuth } from "../../../contexts/AuthProvider/useAuth";
import AreaChart from "../../../charts/area";
import { CheckCheck, CircleHelp, DollarSign, FileUp, Paperclip } from "lucide-react";
import { ExPaper } from "../../../components/Paper";
import { DataTable, ExDataTableProps } from "../../../components/DataTable";
import styled from "styled-components";
import { Column } from "primereact/column";
import { ExButton } from "../../../components/ExButton";
import exportJsonToExcel from "../../../components/JsonToExcel";
import { DashboardPage } from "../../../components/DashboardPage";
import { ExSelectFilter, ExSelectFilterHandler } from "../../../components/ExSelectFilter";
import { getFilesRequisicao, ModalRequisicao } from "../../Requisicoes/utils";
import { Gallery, GalleryHandler } from "../../Gallery";
import { DataTableRowClickEventParams } from "primereact/datatable";
import CircularLoading from "../../../components/CircularLoading";
import { ExCheckBox } from "../../../components/ExCheckBox";

const ContainerDetail = styled.div`
    font-size: 14px;
    padding: 1rem;
    background-color: rgba(0, 0, 0, .075);
    border-radius: 8px;
`

const ToggleButtonSt = styled(ToggleButton)(() => ({
    [`&.${toggleButtonClasses.root}`]: {
        paddingLeft: '24px',
        paddingRight: '24px',
        [`&.${toggleButtonClasses.selected}, &.${toggleButtonClasses.selected}:hover`]: {
            color: "white",
            backgroundColor: 'var(--primary-color)',
            borderLeft: '1px solid rgba(255, 255, 255, .5)',
            borderRight: '1px solid rgba(255, 255, 255, .5)'
        }
    }
}))

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0,
        },
    },
    [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
    {
        marginLeft: isMobileDevice() ? 0 : -1,
        borderLeft: isMobileDevice() ? '' : '1px solid transparent'
    },
}));

interface SituacaoToggleHandler {
    setSituacoes: (val: any) => void
    getValue: () => string[]
}

const SituacaoToggle = forwardRef(({ onChange }: { onChange: () => void }, ref: Ref<SituacaoToggleHandler>) => {
    const [fullSituacoes, setFullSituacoes] = useState<any>()
    const [situacoes, setSituacoes] = useState<string[]>([])

    useImperativeHandle(ref, () => ({
        setSituacoes: (val: any) => setFullSituacoes(val),
        getValue: () => situacoes
    }))

    useEffect(() => {
        onChange()
    }, [situacoes, onChange])

    const handleChange = (event: any, value: any) => {
        setSituacoes(value)
    }

    return (
        <StyledToggleButtonGroup
            value={situacoes}
            color="standard"
            orientation={isMobileDevice() ? 'vertical' : 'horizontal'}
            onChange={handleChange}
        >
            {fullSituacoes?.sort((a: any, b: any) => (a.statsequencia + a.statrejeicao * 100000) - (b.statsequencia + b.statrejeicao * 100000)).map((e: any) =>
                <ToggleButtonSt key={e.statid} value={e.statid}>{e.statdescricao}</ToggleButtonSt>
            )}
        </StyledToggleButtonGroup>
    )
})

const DateInput = forwardRef(({ id, label, onChange, defaultValue }: any, ref) => (
    <Input ref={ref} id={id} type="date" label={label} onAfterChange={onChange} defaultValue={defaultValue} />
));

const DataTableRefered = forwardRef((props: Partial<ExDataTableProps> & { columns: any }, ref) => {
    const [value, setValue] = useState([])
    const [expandedRows, setExpandedRows] = useState<any[]>([]);

    useImperativeHandle(ref, () => ({
        setValue,
        collapseAll: () => setExpandedRows([])
    }))

    const model = props.columns.map((e: any) => ({ id: e.field, label: e.header }))
    const excelIgnore = (value?.length && props.columns) ? Object.keys(value[0]).filter(e => !props.columns.find((el: any) => el.field === e)) : []

    const rowToggleManual = (e: DataTableRowClickEventParams) => {
        setExpandedRows((val) => {
            if (val.includes(e.data))
                return val.filter(el => el !== e.data)
            else
                return [...val, e.data]
        })
    }

    return (
        <>
            <div style={{ display: 'flex', marginTop: isMobileDevice() ? '10px' : 0, alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <ExButton secondary icon={!isMobileDevice() && <FileUp />} style={{ marginRight: '8px' }} onClick={() => { exportJsonToExcel(formatDateTimeFile(new Date()), value, { inputs: model, excelIgnore }) }}>Exportar</ExButton>
            </div>
            <DataTable
                {...props}
                value={value}
                originvalues={value}
                expandedRows={expandedRows}
                onRowToggle={(e: any) => setExpandedRows(e.data)}
                onRowClick={rowToggleManual}>
                <Column expander={true} style={{ fontSize: '14px', width: '2.5rem', marginBottom: '-10px' }} />
                {Columns(props.columns)}
            </DataTable>
        </>
    )
})

const Details = memo(({ row, reqToFilter }: any) => {
    const auth = useAuth();

    const [data, setData] = useState([])

    const refrequisicao = useRef<any>(null)
    const refgallery = useRef<GalleryHandler>(null);

    useEffect(() => {
        auth.get('/v1/requisicaoitensimob?imobid=' + row.imobid).then((_data: any) => {
            const filteredItens = _data.data.filter((e: any) =>
                reqToFilter.find((req: any) => req.reqid === e.reqid && req.usucodigo === e.usucodigo)
            )

            setData(filteredItens)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const procData = data.map((e: any) => ({ ...e, total: e.reqitemquantidade * e.reqitemunitario }))

    const editRequisicao = (item: any) => {
        // auth.get('/v1/requisicaoone?reqid=' + item.reqid + '&usucodigo=' + item.usucodigo).then((e: any) => { 
        //     refrequisicao.current?.openModal({ ...e.data[0], statid: e.data[0].reqsituacao, forid: e.data[0].reqfornecedor })
        // })
        auth.get('/v1/requisicaokanban?reqid=' + item.reqid).then((e: any) => {
            // Gamboarra pra tratar enquanto o usucodigo não sai da chave primária da tabela
            const tempGb = e.data.find((req: any) => req.usucodigo === item.usucodigo)

            refrequisicao.current?.openModal(tempGb)
        })
    }

    const viewFiles = async (e: any) => {
        const files = await getFilesRequisicao(auth, e.reqid, e.usucodigo);
        refgallery.current?.openModal(files, '', e.reqid, false, e.usucodigo);
    }

    const columns = [
        { field: 'reqitemdescricao', header: 'Item' },
        {
            field: 'reqnumero', header: 'Nº Requisição', body: (e: any) =>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => editRequisicao(e)}>{e.reqnumero}</span>
                    {e.filecount > 0 && <ButtonCard hint={'Arquivos'} color={'white'} iconcenter={<Paperclip size={16} />} style={{ marginLeft: '16px' }} onClick={() => viewFiles(e)} className='p-1 me-1' />}
                </div>
        },
        { field: 'reqnumeronotafiscal', header: 'Nota Fiscal' },
        { field: 'reqdata', header: 'Data', body: (e: any) => formatDate(e.reqdata, true) },
        { field: 'reqitemquantidade', header: 'Quantidade', body: (e: any) => formatDecimal(e.reqitemquantidade), dataType: 'numeric' },
        { field: 'reqitemunitario', header: 'Unitário', body: (e: any) => formatDecimal(e.reqitemunitario), dataType: 'numeric' },
        { field: 'total', header: 'Total', body: (e: any) => formatDecimal(e.total), dataType: 'numeric' },
    ];

    const model = columns.map(e => ({ id: e.field, label: e.header }))
    const excelIgnore = procData.length > 0 ? Object.keys(procData[0]).filter(e => !columns.find(el => el.field === e)) : []

    return (
        <ContainerDetail>
            <div style={{ display: 'flex', marginTop: isMobileDevice() ? '10px' : 0, alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <ExButton secondary icon={!isMobileDevice() && <FileUp />} style={{ marginRight: '8px' }} onClick={() => { exportJsonToExcel(formatDateTimeFile(new Date()), procData, { inputs: model, excelIgnore }) }}>Exportar</ExButton>
            </div>
            <DataTable originvalues={procData} value={procData} disabledrefresh={true}>
                {Columns(columns)}
            </DataTable>
            <ModalRequisicao ref={refrequisicao} />
            <Gallery ref={refgallery} />
        </ContainerDetail>
    )
})

export const DashRequisicoes = forwardRef((props: any, ref) => {
    const auth = useAuth();

    const originData = useRef<any[]>([]);

    const [data, setData] = useState<any[]>();

    const refemissaoi = useRef<any>(null);
    const refemissaof = useRef<any>(null);
    const refvencimentoi = useRef<any>(null);
    const refvencimentof = useRef<any>(null);

    const refcheckboximob = useRef<any>(null);
    const refEmpresa = useRef<ExSelectFilterHandler>(null)
    const refCategoria = useRef<ExSelectFilterHandler>(null)
    const refSubcategoria = useRef<ExSelectFilterHandler>(null)
    const refImobilizado = useRef<ExSelectFilterHandler>(null)
    const refSituacoes = useRef<SituacaoToggleHandler>(null)
    const refFornecedor = useRef<ExSelectFilterHandler>(null)
    const refMotivo = useRef<ExSelectFilterHandler>(null)
    const refSolicitante = useRef<ExSelectFilterHandler>(null)

    const refchartimobilizado = useRef<any>(null);
    const refchartcatimobilizado = useRef<any>(null);
    const refchartsubcatimobilizado = useRef<any>(null);
    const refchartmotivos = useRef<any>(null);
    const refchartgrupos = useRef<any>(null)
    const refchartmensal = useRef<any>(null);
    const refcardqtde = useRef<any>(null);
    const refcardtotal = useRef<any>(null);
    const refcardsemcat = useRef<any>(null);
    const refFilter = useRef<any>({})
    const refDataTable = useRef<any>(null)
    const refFileredData = useRef<any>(null)

    useImperativeHandle(ref, () => ({
        setData
    }));

    useEffect(() => {
        if (!data) return;

        const fetchData = async () => {
            try {
                const responseC: any = await auth.get(model_imobilizado_categoria.api_get, { limit: 10000 });
                const responseS: any = await auth.get(model_imobilizado_subcategoria.api_get, { limit: 10000 });
                const responseI: any = await auth.get(model_imobilizado.api_get, { limit: 10000 });
                const responseSit: any = await auth.get(model_status.api_get);

                const formattedDataC = responseC.data.map((item: any) => ({ value: item.imobcatid, label: item.imobcatdescricao }));
                const formattedDataS = responseS.data.map((item: any) => ({ value: item.imobsubcatid, label: item.imobsubcatdescricao }));
                const formattedDataI = responseI.data.map((item: any) => ({ value: item.imobid, label: item.imobdescricao }));

                await refCategoria.current?.setCustomOptions(formattedDataC);
                await refSubcategoria.current?.setCustomOptions(formattedDataS);
                await refImobilizado.current?.setCustomOptions(formattedDataI);
                refSituacoes.current?.setSituacoes(responseSit.data.filter((e: any) => e.statativo === 1))
            } catch (error) {
                toastError(error);
            }
        };

        fetchData();
        originData.current = data;
        prePopulate(
            refemissaoi.current.getValue(),
            refemissaof.current.getValue(),
            refcheckboximob.current.getValue(),
            refvencimentoi.current.getValue(),
            refvencimentof.current.getValue()
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const getNextMultipleOf1000 = (value: number) => Math.ceil(value / 1000) * 1000;

    const getValue = (groupByKey: string | ((item: any) => string), item: any) => typeof groupByKey === 'function' ? groupByKey(item) : item[groupByKey] || 'Não Informado'

    const groupData = (data: any[], groupByKey: string | ((item: any) => string)) =>
        data.reduce((acc: Record<string, number>, item: any) => {
            const key = getValue(groupByKey, item);
            const value = parseFloat((item.total || 0).toFixed(2));
            acc[key] = (acc[key] || 0) + value;
            return acc;
        }, {});

    const populateChart = (refChart: any, data: any, options: { groupByKey: any; chartTitle: string; limit?: number; type?: 'Bar' | 'Pie'; labelFontSize?: string }) => {
        const { groupByKey, chartTitle, limit = Infinity, type = 'Bar', labelFontSize = '14px' } = options;

        var fdataPointSelection

        if (groupByKey) {
            fdataPointSelection = (e: any, ct: any, { w }: any) => {
                const acceptValues = w.globals.selectedDataPoints[0]?.map((i: number) => w.globals.labels[i]).filter((e: any) => e)

                if (!acceptValues || acceptValues.length === 0)
                    delete refFilter.current[groupByKey]
                else
                    refFilter.current[groupByKey] = (e: any) => acceptValues.includes(getValue(groupByKey, e))

                prePopulate(
                    refemissaoi.current.getValue(),
                    refemissaof.current.getValue(),
                    refcheckboximob.current.getValue(),
                    refvencimentoi.current.getValue(),
                    refvencimentof.current.getValue()
                )
            }
        }

        const filterArr = Object.entries(refFilter.current)
        data = [...data].filter((e: any) => filterArr.every(([key, val]: any) => key === groupByKey || val(e)))

        const groupedData = groupData(data, groupByKey);

        let entries: [string, number][];

        if (typeof groupByKey === 'function')
            entries = Object.entries(groupedData).sort(([keyA], [keyB]) => {
                const dateA = moment(keyA, 'MM/YYYY');
                const dateB = moment(keyB, 'MM/YYYY');
                return dateA.diff(dateB);
            }).slice(0, limit);
        else
            entries = Object.entries(groupedData).sort(([, a], [, b]) => b - a).slice(0, limit);

        const labels = entries.map(([key]) => key);
        const values = entries.map(([, value]) => value);

        if (type === 'Bar') {
            refChart.current?.setOptions(labels, getNextMultipleOf1000(Math.max(...values)), chartTitle, undefined, undefined, undefined, labelFontSize, fdataPointSelection);
            refChart.current?.setSeries([{ name: chartTitle, data: values }]);
        } else if (type === 'Pie') {
            refChart.current?.setLabels(labels);
            refChart.current?.setOptions(fdataPointSelection);
            refChart.current?.setSeries(values);
        }
    };

    const setCardValues = (ref: any, value: any) => {
        ref.current?.setValue(value);
    };

    const filterByDate = (data: any[], startDate?: any, endDate?: any, dateField: string = 'reqdata') =>
        data.filter((item: any) => {
            const dateValue = item[dateField];
            const date = formatDataCompare(dateValue);
            return (!startDate || date >= startDate) && (!endDate || date <= endDate);
        });

    const prePopulateDefault = () => {
        prePopulate(
            refemissaoi.current.getValue(),
            refemissaof.current.getValue(),
            refcheckboximob.current.getValue(),
            refvencimentoi.current.getValue(),
            refvencimentof.current.getValue()
        )
    }

    const prePopulate = (startDate?: any, endDate?: any, onlyImobNotCat?: any, iVencimento?: any, fVencimento?: any) => {
        var filteredData = filterByDate(originData.current, startDate, endDate, 'reqdata');
        filteredData = filterByDate(filteredData, iVencimento, fVencimento, 'reqdtvencimento');

        filteredData = refEmpresa.current?.applyFilter(filteredData) || filteredData
        filteredData = refCategoria.current?.applyFilter(filteredData) || filteredData
        filteredData = refSubcategoria.current?.applyFilter(filteredData) || filteredData
        filteredData = refImobilizado.current?.applyFilter(filteredData) || filteredData
        filteredData = refFornecedor.current?.applyFilter(filteredData) || filteredData
        filteredData = refMotivo.current?.applyFilter(filteredData) || filteredData
        filteredData = refSolicitante.current?.applyFilter(filteredData) || filteredData

        const fSituacoes = refSituacoes.current?.getValue()

        if (fSituacoes && fSituacoes.length > 0)
            filteredData = filteredData.filter((item: any) => fSituacoes.includes(item.reqsituacao))

        if (onlyImobNotCat === 1)
            filteredData = filteredData.filter((item: any) => item.imobcatid === null);

        const filterArr = Object.entries(refFilter.current)
        const cardData = filteredData.filter((e: any) => filterArr.every(([key, val]: any) => val(e)))

        refFileredData.current = cardData
        refDataTable.current?.collapseAll()

        setCardValues(refcardqtde, cardData.length || 0);
        setCardValues(refcardtotal, formatMonetary(cardData.reduce((sum: any, item: any) => sum + item.total, 0)));
        setCardValues(refcardsemcat, formatMonetary(
            cardData.filter((item: any) => !item.imobcatdescricao).reduce((sum: any, item: any) => sum + item.total, 0)
        ));

        populateChart(refchartmensal, filteredData, {
            groupByKey: (item: any) => moment(item.reqdata).format('MM/YYYY'),
            chartTitle: 'Valor por Mês',
        });

        populateChart(refchartimobilizado, filteredData, {
            groupByKey: 'imobdescricao',
            chartTitle: 'Top 15 - Valor por Imobilizado',
            limit: 15,
        });

        populateChart(refchartmotivos, filteredData, {
            groupByKey: 'motdescricao',
            chartTitle: 'Motivos',
            type: 'Pie',
        });

        populateChart(refchartgrupos, filteredData, {
            groupByKey: 'grudescricao',
            chartTitle: 'Grupos',
            type: 'Pie',
        });

        populateChart(refchartcatimobilizado, filteredData.filter((item: any) => item.imobcatdescricao), {
            groupByKey: 'imobcatdescricao',
            chartTitle: 'Valor por Categoria de Imobilizado',
        });

        populateChart(refchartsubcatimobilizado, filteredData.filter((item: any) => item.imobsubcatdescricao), {
            groupByKey: 'imobsubcatdescricao',
            limit: 10,
            chartTitle: 'Top 10 - Valor por Subcategoria de Imobilizado',
        });

        const grouped = groupData(cardData, 'imobid')
        const obj = Object.entries(grouped).map(([key, val]) => {
            const dadosImob = cardData.find(e => e.imobid === key)
            return ({ ...dadosImob, imobid: key, total: val })
        })

        refDataTable.current.setValue(obj)
    };

    const columns = [
        { field: 'imobdescricao', header: 'Imobilizado', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
        { field: 'imobidentificacao', header: 'Identificação', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
        { field: 'imobcatdescricao', header: 'Categoria', style: { width: window.innerWidth, whiteSpace: 'pre-wrap' } },
        { field: 'imobsubcatdescricao', header: 'Subcategoria' },
        { field: 'total', header: 'Total', body: (e: any) => formatDecimal(e.total), dataType: 'numeric' },
    ];

    return (
        !data ? <CircularLoading /> :
            <div style={{ padding: isMobileDevice() ? '1rem' : '1rem 4rem' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} >
                        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Grid item xs={isMobileDevice() ? 12 : 1.5}>
                                <DateInput ref={refemissaoi} id="dash_req_emis_inicial" label="Emissão Inicial" onChange={(value: any) => prePopulate(value, refemissaof.current.getValue(), refcheckboximob.current.getValue(), refvencimentoi.current.getValue(), refvencimentof.current.getValue())} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 1.5}>
                                <DateInput ref={refemissaof} id="dash_req_emis_final" label="Emissão Final" onChange={(value: any) => prePopulate(refemissaoi.current.getValue(), value, refcheckboximob.current.getValue(), refvencimentoi.current.getValue(), refvencimentof.current.getValue())} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 1.5}>
                                <DateInput ref={refvencimentoi} id="dash_req_venc_inicial" label="Vencimento Inicial" onChange={(value: any) => prePopulate(refemissaoi.current.getValue(), refemissaof.current.getValue(), refcheckboximob.current.getValue(), value, refvencimentof.current.getValue())} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 1.5}>
                                <DateInput ref={refvencimentof} id="dash_req_venc_final" label="Vencimento Final" onChange={(value: any) => prePopulate(refemissaoi.current.getValue(), refemissaof.current.getValue(), refcheckboximob.current.getValue(), refvencimentoi.current.getValue(), value)} />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2.4}>
                                <ExSelectFilter
                                    ref={refSolicitante}
                                    model={model_usuario}
                                    label={'Solicitante'}
                                    filterField="reqsolicitante"
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 3} style={{ marginTop: '24px' }} >
                                <ExCheckBox ref={refcheckboximob} id="checkimob" label="Apenas Imobilizados sem Categoria" onClick={(e: any) => prePopulate(refemissaoi.current.getValue(), refemissaof.current.getValue(), e.target.checked ? 1 : 0, refvencimentoi.current.getValue(), refvencimentof.current.getValue())} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} >
                        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refEmpresa}
                                    model={model_empresa}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refCategoria}
                                    model={model_imobilizado_categoria}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refSubcategoria}
                                    model={model_imobilizado_subcategoria}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refImobilizado}
                                    model={model_imobilizado}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refFornecedor}
                                    model={model_fornecedor}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            <Grid item xs={isMobileDevice() ? 12 : 2}>
                                <ExSelectFilter
                                    ref={refMotivo}
                                    model={model_motivo_requisicao}
                                    onAfterChange={() => prePopulateDefault()}
                                />
                            </Grid>
                            {/* <Grid item xs={isMobileDevice() ? 12 : 1}>
                            <ExButton secondary icon={!isMobileDevice() && <FileUp />} style={{ marginRight: '8px' }} onClick={() => { }}>Limpar</ExButton>
                        </Grid> */}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SituacaoToggle ref={refSituacoes} onChange={() => prePopulateDefault()} />
                    </Grid>
                </Grid>

                <DashboardPage name="Requisições">
                    <Grid container spacing={4}>
                        <Grid item xs={isMobileDevice() ? 12 : 8}>
                            <BarChart ref={refchartcatimobilizado} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '450px' }}>
                                <PieChart ref={refchartgrupos} title='Grupos' monetary />
                            </div>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 8}>
                            <BarChart ref={refchartsubcatimobilizado} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 4}>
                            <div style={{ height: '450px' }}>
                                <PieChart ref={refchartmotivos} title='Motivos' monetary />
                            </div>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <BarChart ref={refchartimobilizado} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <Grid container spacing={2}>
                                <GridCard md={4}>
                                    <ExCard ref={refcardqtde} title='Quantidade Total' icon={<CheckCheck />} />
                                </GridCard>
                                <GridCard md={4}>
                                    <ExCard ref={refcardtotal} title='Valor Total' icon={<DollarSign />} />
                                </GridCard>
                                <GridCard md={4}>
                                    <ExCard ref={refcardsemcat} title='Total sem Categoria' icon={<CircleHelp />} />
                                </GridCard>
                            </Grid>
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 12 : 12}>
                            <AreaChart ref={refchartmensal} />
                        </Grid>
                    </Grid>
                </DashboardPage>

                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <ExPaper>
                            <DataTableRefered
                                ref={refDataTable}
                                disabledrefresh={true}
                                columns={columns}
                                sortField={'total'}
                                sortOrder={-1}
                                rowExpansionTemplate={
                                    (row) => <Details row={row} reqToFilter={refFileredData.current} />
                                }
                            />
                        </ExPaper>
                    </Grid>
                </Grid>
            </div>
    );
});